import CryptoJS from 'crypto-js'

import api from '@/api'
import { KEY_SOS_DETAIL_ID, KEY_THIRD_PARTY_CONFIG } from '@/fixtures/Constants'
import { CRYPTO_KEY } from '@/fixtures/Hash'
import { convertFileSize } from '@/utils/fileUtils'

const encryptedThirdPartyConfig = btoa(KEY_THIRD_PARTY_CONFIG)
const encryptedSosDetailId = btoa(KEY_SOS_DETAIL_ID)

const namespaced = true

const state = {
  thirdPartyConfig: localStorage.getItem(encryptedThirdPartyConfig) || null,
  sosPreview: [],
  sosList: [],
  sosDetailId: localStorage.getItem(encryptedSosDetailId) || null,
  sosDetail: []
}

const getters = {
  thirdPartyConfig: state => state.thirdPartyConfig
    ? JSON.parse(CryptoJS.AES.decrypt(state.thirdPartyConfig, CRYPTO_KEY).toString(CryptoJS.enc.Utf8))
    : null,
  sosPreview: state => state.sosPreview,
  sosList: state => state.sosList,
  sosDetailId: state => state.sosDetailId
    ? Number(CryptoJS.AES.decrypt(state.sosDetailId, CRYPTO_KEY).toString(CryptoJS.enc.Utf8))
    : null,
  sosDetail: state => state.sosDetail
}

const mutations = {
  SET_THIRD_PARTY_CONFIG: (state, payload) => {
    state.thirdPartyConfig = payload
    localStorage.setItem(encryptedThirdPartyConfig, payload)
  },
  SET_SOS_PREVIEW: (state, payload) => {
    state.sosPreview = payload
  },
  SET_SOS_LIST: (state, payload) => {
    state.sosList = payload
  },
  SET_SOS_DETAIL_ID: (state, payload) => {
    state.sosDetailId = payload
    localStorage.setItem(encryptedSosDetailId, payload)
  },
  SET_SOS_DETAIL: (state, payload) => {
    state.sosDetail = payload
  }
}

const actions = {
  async postThirdPartyConfig({ commit }, payload) {
    try {
      await api.postThirdPartyConfig(payload)
      commit('SET_THIRD_PARTY_CONFIG', CryptoJS.AES.encrypt(JSON.stringify(payload), CRYPTO_KEY).toString())
    } catch ({ data }) {
      throw data
    }
  },
  async uploadExcel({ commit, dispatch }, payload) {
    try {
      const formData = new FormData()
      formData.append('sos', payload)

      // add start progress to change component state immediately
      commit('UploadFile/SET_PROGRESS', 0.25, { root: true })
      const { data } = await api.uploadFile(formData, (progress) => {
        const totalLength = progress.lengthComputable
          ? progress.total
          : progress.target.getResponseHeader('content-length')
            || progress.target.getResponseHeader('x-decompressed-content-length')

        commit('UploadFile/SET_PROGRESS', totalLength
          ? Math.round((progress.loaded * 100) / totalLength)
          : 100,
        { root: true })
      })

      if (data.error) {
        commit('UploadFile/SET_PROGRESS', 0, { root: true })
        commit('UploadFile/SET_IS_UPLOAD_ERROR', true, { root: true })
        return
      }

      commit('UploadFile/SET_FILE_NAME', data.file_name, { root: true })
      commit('UploadFile/SET_FILE_SIZE', convertFileSize(payload.size), { root: true })
      commit('UploadFile/SET_IS_UPLOAD_ERROR', false, { root: true })
      commit('SET_SOS_PREVIEW', data.data.length ? data.data : [])
    } catch (error) {
      commit('UploadFile/SET_PROGRESS', 0, { root: true })
      throw error
    }
  },
  async getSosList({ commit, rootGetters }) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const filter = {
        search: filterConfig.search,
        page: filterConfig.currentPage,
        limit: filterConfig.pageSize
      }
      const { data } = await api.getSos(null, filter)
      if (data.error) {
        // temporary, waiting implementation of API
        commit('SET_SOS_LIST', [])
        commit('Table/SET_TOTAL_DATA', 0, { root: true })
        commit('Table/SET_TOTAL_PAGE', 1, { root: true })
        return
      }
      commit('SET_SOS_LIST', data.data)
      commit('Table/SET_TOTAL_DATA', data.total, { root: true })
      commit('Table/SET_TOTAL_PAGE', data.pages, { root: true })
    } catch (error) {
      throw error
    }
  },
  async getSosDetail({ commit, dispatch, rootGetters}, payload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const filter = {
        search: filterConfig.search,
        page: filterConfig.currentPage,
        limit: filterConfig.pageSize
      }
      const { data } = await api.getSos(payload, filter)
      commit('SET_SOS_DETAIL_ID', CryptoJS.AES.encrypt(payload.toString(), CRYPTO_KEY).toString())
      if (data.error) {
        commit('SET_SOS_DETAIL', await dispatch('transformDetailData', []))
        return
      }
      commit('SET_SOS_DETAIL', await dispatch('transformDetailData', data.data.length ? data.data : []))
    } catch (error) {
      throw error
    }
  },
  async postSos({ dispatch }, payload) {
    try {
      const data = await api.postSos(payload)
      await dispatch('UploadFile/reset', {}, { root: true })

      return data
    } catch (error) {
      throw error
    }
  },
  transformDetailData({}, data) {
    return data.length
      ? data.map((
        { id,
          sos_list_id,
          created_at,
          created_by,
          updated_at,
          updated_by,
          deleted_at,
          deleted_by,
          ...item
        }) => ({ ...item }))
      : []
  }
}

export default { namespaced, state, getters, mutations, actions }
